import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { Loader } from '../../loader/loader';
import { SnackbarService } from '../../services/snackbar.service';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { Labels } from '../../constants/labels-constant';
import { SearchableDropdown } from '../../components/searchable-dropdown/searchable-dropdown';
import { ReportV2Service, RecipeSearchItem } from '../../services/report-v2.service';

@Component({
  selector: 'app-create-report-type',
  templateUrl: './create-report-type.html',
  styleUrls: ['./create-report-type.scss'],
  imports: [
    MatStepperModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    CommonModule,
    Loader,
    SearchableDropdown,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateReportTypeComponent {
  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
  ) {
  }
  private fb = inject(FormBuilder);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);
  isNextButtonDiabled: boolean = true;
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;
  isMessageDialogOpen = false;
  filterErrorMessages: string = '';
  dialogTexts = DialogueTexts;
  labels = Labels;

  reportTypes = this.reportV2Service.getReportTypes();
  clients = this.reportV2Service.getClients();

  isLoading = false;
  searchResults = signal<RecipeSearchItem[]>([]);
  selectedRecipeId: number | null = null;

  options: FormGroup = this.fb.group({
    reportType: ['', [Validators.required]],
    clientName: ['', [Validators.required]],
  });

  private clientsEffect = effect(() => {
    const clientList = this.clients();
    if (clientList.length > 0 && !this.options.get('clientName')?.value) {
      this.options.get('clientName')?.setValue(clientList[0].clientId);
    }
  });

  private reportTypesEffect = effect(() => {
    const typeList = this.reportTypes();
    if (typeList.length > 0 && !this.options.get('reportType')?.value) {
      this.options.get('reportType')?.setValue(typeList[0].id);
    }
  });

  ngOnInit(): void {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.reportV2Service.loadClients();

    this.options.get('clientName')?.valueChanges.pipe(distinctUntilChanged()).subscribe((clientId) => {
      if (clientId) {
        this.options.get('reportType')?.setValue('');
        this.reportV2Service.loadReportTypes(clientId);
      }
    });
  }

  ngOnDestroy(): void {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  closeMessageDialog() {
    this.isMessageDialogOpen = false;
  }

  selectRecipe(recipe: RecipeSearchItem) {
    this.selectedRecipeId = recipe.recipeId;
    this.isNextButtonDiabled = false;
  }

  step = signal(1);

  setStep(newStep: number) {
    this.step.set(newStep);
  }

  findRecipes(event?: Event): void {
    this.options.markAllAsTouched();
    if (this.options.invalid) {
      return;
    }
    if (event) {
      event.preventDefault();
    }
    const values = this.options.getRawValue();
    const clientId = Number(values.clientName);
    const reportTypeId = Number(values.reportType);

    this.isLoading = true;
    this.reportV2Service.searchRecipes(clientId, reportTypeId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (!response?.success) {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response?.message ?? 'No recipes found.',
          });
          this.searchResults.set([]);
          return;
        }
        if (!response.data || response.data.length === 0) {
          this.isMessageDialogOpen = true;
          this.filterErrorMessages = response.message || 'No recipes found.';
          this.searchResults.set([]);
          return;
        }
        this.searchResults.set(response.data);
        this.cd.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        this.cd.markForCheck();
        const apiMessage = error?.error?.message;
        if (error.status === 404) {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: apiMessage ?? 'No recipes found.',
          });
        } else if (error.status === 403) {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: apiMessage ?? 'Access denied. You do not have access to this client.',
          });
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: apiMessage ?? this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.searchResults.set([]);
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/reports']);
  }

  onReset(): void {
    this.isNextButtonDiabled = true;
    this.selectedRecipeId = null;
    this.reportV2Service.clearReportTypes();
    this.options.reset();
    this.options.get('clientName')?.setValue('');
    this.options.get('reportType')?.setValue('');
    this.searchResults.set([]);
  }

  generateReport() {
    if (this.selectedRecipeId) {
      this.router.navigate(['/generate-report'], {
        queryParams: { recipeId: this.selectedRecipeId },
      });
    }
  }
}
